import React, { useState } from 'react';
import * as Linking from 'expo-linking';
import { ActionButtonLayout } from '../../../../../../v2BaseComponents/ActionButtonLayout';
import { getIntl } from '../../../../../../../utils/intl';
import Text from '../../../../../../v2BaseComponents/Text';
import MainContentContainer from '../../../../../../v2BaseComponents/MainContentContainer';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import useOrganizationPaymentIntegration from '../../../../../../../hooks/api/useOrganizationPaymentIntegration';
import usePreRedirectArgs from '../../../../../../../hooks/usePreRedirectArgs';
import { useIsOrganizationOwner } from '../../../../../../../hooks/api/useOrganization';
import Stack from '../../../../../../v2BaseComponents/Stack';
import { AlertIcon } from '../../../../../../v2BaseComponents/Icon';
import MissingBrandingAlert from '../MissingBrandingAlert';
import { useOrganizationTabNavigation } from '../../../../../../../utils/appNavigationNames';
import { useIsMobileLayout } from '../../../../../../../hooks/useIsMobileLayout';

const intl = getIntl('client-billing-tab-setup', {
    manageStripeAccount: 'Manage Stripe Account',
    loaderMessage: 'Connecting to your Stripe account...',
    header: 'Your Account is Active',
    orgOwnerSubHeader:
        "Your Stripe account is currently in a active state.  You are now able to invoice your organization's clients.  You can view your Stripe dashboard to view and manage pending and paid invoices.",
    nonOwnerSubHeader:
        "Your Stripe account is currently in a active state.  You are now able to invoice your organization's clients.  Note that only the organization owner has access to your organization's Stripe dashboard.",
    updateBranding: 'Update Branding',
});

export type SetupContentOwnProps = {};

export type SetupContentProps = SetupContentOwnProps;

const SetupContent: React.FC<SetupContentProps> = () => {
    const isMobile = useIsMobileLayout();
    const [isConnecting, setIsConnecting] = useState(false);
    const isOrganizationOwner = useIsOrganizationOwner();
    const { setPreRedirectArgs } = usePreRedirectArgs();
    const organizationNavigation = useOrganizationTabNavigation();
    const { isRetrievingDashboardUrl, retrieveDashboardUrl } = useOrganizationPaymentIntegration({
        onRetrieveDashboardUrlSuccess: (setupLink) => {
            setPreRedirectArgs(
                {
                    type: 'stripe-connected-account',
                },
                () => {
                    setIsConnecting(true);
                    Linking.openURL(setupLink);
                },
            );
        },
    });

    return (
        <AnimateLoadedContent
            isLoading={isRetrievingDashboardUrl || isConnecting}
            message={isConnecting ? intl('loaderMessage') : undefined}
        >
            {() => (
                <ActionButtonLayout
                    scrollable
                    actions={[
                        {
                            title: intl('updateBranding'),
                            onPress: () => organizationNavigation.navigate('edit-client-billing-branding', {}),
                            variant: 'secondary',
                            testID: 'update-branding-button',
                            displayInMoreMenu: isMobile,
                            icon: 'Edit2',
                        },
                        {
                            title: intl('manageStripeAccount'),
                            icon: 'Settings',
                            onPress: retrieveDashboardUrl,
                            variant: 'primary',
                            testID: 'manage-stripe-account-button',
                            show: isOrganizationOwner,
                        },
                    ]}
                >
                    <MainContentContainer style={{ minHeight: '100%', minWidth: '100%' }}>
                        <Stack vertical justify="center" align="center" space={20} fullHeight fullWidth>
                            <Stack vertical justify="center" align="center" space={4}>
                                <Text variant="bold-h4">{intl('header')}</Text>
                                <AlertIcon size="lg" name="CheckCircle" variant="success" />
                                <Text variant="body-1" alignText="center">
                                    {intl(isOrganizationOwner ? 'orgOwnerSubHeader' : 'nonOwnerSubHeader')}
                                </Text>
                                <MissingBrandingAlert />
                            </Stack>
                        </Stack>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(SetupContent);
